// react-redux imports
import { MouseEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/store";

// react-select imports
import { OptionTypeBase } from "react-select";

// styled-components imports
import UserInputSection from "../styled-components/UserInputSection";
import StyledButtonWrapper from "../styled-components/StyledButtonWrapper";
import FuelTypeSection from "./styled-components/FuelTypeSection";

// common imports
import FlexSpan from "common/components/styled-components/FlexSpan";
import PopUpContentGroup from "common/components/styled-components/PopUpContentGroup";
import PopUpParagraph from "common/components/styled-components/PopUpParagraph";
import PopUpTitle from "common/components/styled-components/PopUpTitle";
import SectionTitle from "common/components/styled-components/SectionTitle";
import SectionSubtitle from "common/components/styled-components/SectionSubtitle";
import StandardButton from "common/components/styled-components/StandardButton";
import Select from "common/components/Select";
import Tooltip from "common/components/Tooltip";
import TooltipPopUp from "common/components/TooltipPopUp";

// local imports
import {
  FuelType,
  CAR_FUEL_TYPES,
  DISTANCE_OPTIONS,
  MOTORCYCLE_FUEL_TYPES,
} from "../constants/transport";

import {
  CarFuelOptions,
  MotorcycleFuelOptions,
  setCarFuelType,
  setCarDistanceWeekly,
  setMotorcycleFuelType,
  setMotorcycleDistanceWeekly,
} from "./transportSlice";
import FlexColumn from "common/components/styled-components/FlexColumn";
import FuelTypeButtons from "./styled-components/FuelTypeButtons";
import { TOOLTIP_ACTIONS } from "common/constants/tooltip";

interface Tooltips {
  title: boolean;
  carFuelType: boolean;
  motorcycleFuelType: boolean;
}

type TooltipAction = "open" | "close" | "toggle";

const initialTooltipsState: Tooltips = {
  title: false,
  carFuelType: false,
  motorcycleFuelType: false,
};

function StepOne() {
  const [tooltips, setTooltips] = useState<Tooltips>(initialTooltipsState);

  const dispatch = useDispatch();
  const upDesktopScreen = useSelector(
    (state: RootState) => state.app.upDesktopScreen
  );

  const carFuelType = useSelector(
    (state: RootState) => state.calculator.transport.carFuelType
  );

  const carDistanceWeekly = useSelector(
    (state: RootState) => state.calculator.transport.carDistanceWeekly
  );

  const motorcycleFuelType = useSelector(
    (state: RootState) => state.calculator.transport.motorcycleFuelType
  );

  const motorcycleDistanceWeekly = useSelector(
    (state: RootState) => state.calculator.transport.motorcycleDistanceWeekly
  );

  function handleCarFuelTypeSelection(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    const value = e.currentTarget.value as CarFuelOptions;
    if (value === carFuelType) dispatch(setCarFuelType(null));
    else dispatch(setCarFuelType(value));
  }

  function handleMotorcycleFuelTypeSelection(e: MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    const value = e.currentTarget.value as MotorcycleFuelOptions;
    if (value === motorcycleFuelType) dispatch(setMotorcycleFuelType(null));
    else dispatch(setMotorcycleFuelType(value));
  }

  function handleCarDistanceChange(value: OptionTypeBase | null) {
    dispatch(setCarDistanceWeekly(value?.value ?? null));
  }

  function handleMotorcycleDistanceChange(value: OptionTypeBase | null) {
    dispatch(setMotorcycleDistanceWeekly(value?.value ?? null));
  }

  function handleTooltip(tooltip: keyof Tooltips, action: TooltipAction) {
    switch (action) {
      case "open":
        return () => setTooltips({ ...initialTooltipsState, [tooltip]: true });

      case "close":
        return () => setTooltips({ ...initialTooltipsState });

      case "toggle":
        return () =>
          setTooltips({
            ...initialTooltipsState,
            [tooltip]: !tooltips[tooltip],
          });

      default:
        return () => undefined;
    }
  }

  return (
    <>
      <SectionTitle>
        <span>
          Cuando te desplazas, ¿cuántos km haces en estos transportes a la{" "}
          <FlexSpan>
            semana?&nbsp;
            <Tooltip onClick={handleTooltip("title", "toggle")} />
          </FlexSpan>
        </span>
        {tooltips.title && (
          <TooltipPopUp
            title="¿Cuántos Km haces en estos transportes a la semana?"
            handleClose={handleTooltip("title", TOOLTIP_ACTIONS.CLOSE)}
          >
            <PopUpParagraph>
              Es importante conocer en qué medios te desplazas ya que el
              transporte constituye el 25% de las emisiones totales de gases de
              efecto invernadero en España
            </PopUpParagraph>
          </TooltipPopUp>
        )}
        {(tooltips.carFuelType || tooltips.motorcycleFuelType) && (
          <TooltipPopUp
            title="¿Qué tipo de combustible usas?"
            handleClose={handleTooltip(
              tooltips.carFuelType ? "carFuelType" : "motorcycleFuelType",
              "close"
            )}
          >
            <PopUpContentGroup>
              <PopUpTitle>Gasolina</PopUpTitle>
              <PopUpParagraph>
                La gasolina es una mezcla de hidrocarburos que se obtiene del
                petróleo mediante un proceso que se conoce con el nombre de
                destilación fraccionada.
              </PopUpParagraph>
            </PopUpContentGroup>
            <PopUpContentGroup>
              <PopUpTitle>Gasóleo</PopUpTitle>
              <PopUpParagraph>
                El diésel o dísel, también denominado gasóleo o gasoil, es un
                hidrocarburo líquido de densidad sobre 850 kg/m³
              </PopUpParagraph>
            </PopUpContentGroup>
            <PopUpContentGroup>
              <PopUpTitle>Híbrido</PopUpTitle>
              <PopUpParagraph>
                Combina un motor de combustión interna y uno o varios motores
                eléctricos
              </PopUpParagraph>
            </PopUpContentGroup>
            <PopUpContentGroup>
              <PopUpTitle>Eléctrico</PopUpTitle>
              <PopUpParagraph>
                Impulsado por uno o varios motores alimentados por una fuente de
                energía eléctrica
              </PopUpParagraph>
            </PopUpContentGroup>
          </TooltipPopUp>
        )}
      </SectionTitle>
      <UserInputSection>
        <FlexColumn gap={upDesktopScreen ? 23 : 20}>
          <Select
            name="carFuelType"
            label="Coche"
            isSearchable={false}
            options={DISTANCE_OPTIONS}
            placeholder="Selecciona opción"
            value={DISTANCE_OPTIONS.find(
              (option) => option.value === carDistanceWeekly
            )}
            onChange={handleCarDistanceChange}
            isClearable
          />
          <FuelTypeSection>
            <SectionSubtitle>
              ¿Qué tipo de combustible usas?&nbsp;
              <Tooltip
                onClick={handleTooltip("carFuelType", "toggle")}
                size={12}
              />
            </SectionSubtitle>
            <FuelTypeButtons>
              {Object.values(CAR_FUEL_TYPES).map((fuel: FuelType) => (
                <StyledButtonWrapper key={`car_fuel_${fuel.value}`}>
                  <StandardButton
                    value={fuel.value}
                    selected={carFuelType === fuel.value}
                    onClick={handleCarFuelTypeSelection}
                  >
                    {fuel.label}
                  </StandardButton>
                </StyledButtonWrapper>
              ))}
            </FuelTypeButtons>
          </FuelTypeSection>
        </FlexColumn>
        <FlexColumn gap={upDesktopScreen ? 23 : 20}>
          <Select
            name="motorcycleFuelType"
            label="Motocicleta"
            isSearchable={false}
            options={DISTANCE_OPTIONS}
            placeholder="Selecciona opción"
            value={DISTANCE_OPTIONS.find(
              (option) => option.value === motorcycleDistanceWeekly
            )}
            onChange={handleMotorcycleDistanceChange}
            isClearable
          />
          <FuelTypeSection>
            <SectionSubtitle>
              ¿Qué tipo de combustible usas?&nbsp;
              <Tooltip
                size={12}
                onClick={handleTooltip("motorcycleFuelType", "toggle")}
              />
            </SectionSubtitle>
            <FuelTypeButtons>
              {Object.values(MOTORCYCLE_FUEL_TYPES).map((fuel: FuelType) => (
                <StyledButtonWrapper key={`car_fuel_${fuel.value}`}>
                  <StandardButton
                    value={fuel.value}
                    selected={motorcycleFuelType === fuel.value}
                    onClick={handleMotorcycleFuelTypeSelection}
                  >
                    {fuel.label}
                  </StandardButton>
                </StyledButtonWrapper>
              ))}
            </FuelTypeButtons>
          </FuelTypeSection>
        </FlexColumn>
      </UserInputSection>
    </>
  );
}

export default StepOne;
